import React, { PureComponent } from "react";
import { withStyles } from "@material-ui/core/styles";
import {
  Typography,
  Grid,
  Box,
  Paper,
  IconButton,
  Button,
  Tooltip
} from "@material-ui/core";
import { ToggleButton, ToggleButtonGroup } from "@material-ui/lab";
import { Delete as DeleteIcon, HelpOutline } from "@material-ui/icons";
import { isEqual } from "lodash";
import { withErrorBoundary } from "BaseApp/ErrorBoundary/ErrorBoundary";
import { GenericWavefront } from "components/GenericWavefront/GenericWavefront";
import { GenericImageWavefront } from "components/GenericImageWavefront/GenericImageWavefront";
import Plotbox from "MetaCell/containers/SimulateCanvas/components/Plotbox";
import AnalysisApi from "MetaComponent/api/Analysis";
import Wavefront from "MetaComponent/containers/TargetCanvas/components/Wavefront/Wavefront";
import Spinner from "components/Spinner/Spinner";
import Efficiency from "./components/Efficiency/Efficiency";
import { MtfPlotGenerator } from "./components/MTF/MtfPlotGenerator";
import SweepPoint from "components/SweepPoint/SweepPoint";
import DirectionSnackbar from "components/Snackbar/Snackbar";
import PlotUtils from "MetaComponent/helper/PlotUtils";
import PlotActions from "components/PlotActions/PlotActions";
import Helper from "MetaCell/helper/SimulationResult";

const CustomToggleButton = withStyles({
  root: {
    color: "black",
    "&.Mui-selected": {
      backgroundColor: "#3f51b5",
      color: "white",
      "&:hover": {
        backgroundColor: "#3f51b5" // so that nothing happens on hover
      }
    }
  }
})(ToggleButton);

export const styles = {
  root: {
    marginBottom: 40,
    paddingRight: 260
  },
  title: {
    textAlign: "center"
  },
  centerElement: {
    display: "block",
    margin: "auto"
  },
  mainContainer: {
    marginTop: 20
  },
  controlsContainer: {
    width: "60%",
    margin: "auto"
  },
  spinnerContainer: {
    marginBottom: 20
  },
  viewContainer: {
    width: "100%"
  },
  centeredWavefront: {
    margin: "auto"
  },
  plotContainer: {
    padding: 16,
    width: "100%",
    maxWidth: 900,
    margin: "auto"
  },
  plotWrapper: {
    height: 500,
    width: "100%"
  }
};

/**
 * Renders the results of an analysis job, including wavefronts and MTF plots.
 * It manages fetching data, user interactions for sweeping variables, and plot generation.
 */
export class AnalysisJobResult extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      activePlotView: "results",
      sweptVariableValues: {},
      ffwf: null,
      ffwf_image: null,
      nfwf: null,
      nfwf_phase_image: null,
      nfwf_amplitude_image: null,
      fourier: null,
      fourier_image: null,
      loadingResult: false,
      ffwfWidth: null,
      ffwfHeight: null,
      ffEfficiency: null,
      nfEfficiency: null,
      fourrierEfficiency: null,
      rows_count: null,
      columns_count: null,
      ffwf_cross_section_orientation: "xz",
      ffwf_cross_section_index: null,
      nfwf_cross_section_orientation: "xz",
      nfwf_cross_section_index: null,
      nfwf_cross_section_type: "Amplitude",
      fourier_cross_section_orientation: "xz",
      fourier_cross_section_index: null,
      mtfPlots: [],
      loadingMtfPlots: false,
      isCreatingPlot: false,
      showDiffractionLimit: {},
      errorMessage: null,
      cropped_nfwf_width: null,
      cropped_nfwf_height: null,
      nf_center_x: null,
      nf_center_y: null
    };
  }
  componentDidMount() {
    this.setSelectedValues();
    this.getResult();
    this.fetchMtfPlots();
  }

  componentDidUpdate(prevProps) {
    if (!isEqual(prevProps.sweptVariables, this.props.sweptVariables)) {
      this.setSelectedValues();
    }
  }

  /**
   * Fetches all MTF plots associated with the current analysis job.
   */
  fetchMtfPlots = async () => {
    this.setState({ loadingMtfPlots: true });
    try {
      const plots = await AnalysisApi.getMtfPlots(this.props.jobId);
      this.setState({ mtfPlots: plots || [], loadingMtfPlots: false });
    } catch (error) {
      this.setState({
        loadingMtfPlots: false,
        errorMessage:
          "Failed to load MTF plots. Please try refreshing the page."
      });
    }
  };

  /**
   * Enriches swept variable data with their backend location strings (e.g., 'dz' -> 'center_coord.2').
   * This is passed to the MTF plot generator.
   */
  getSweptVariablesWithLocations = () => {
    const {
      sweptVariables,
      setPoint,
      ffwfDx,
      ffwfDy,
      ffwfDz,
      angular_center_angle_x,
      angular_center_angle_y,
      opening_angle_x,
      opening_angle_y,
      euler_angles
    } = this.props;

    if (!sweptVariables || !setPoint) {
      return [];
    }

    const locationMap = {};

    if (setPoint.incident_light) {
      for (const [key, value] of Object.entries(setPoint.incident_light)) {
        if (typeof value === "string" && value.startsWith("=")) {
          locationMap[value.substring(1)] = `incident_light.${key}`;
        }
      }
    }
    if (setPoint.diffractive_order) {
      for (const [key, value] of Object.entries(setPoint.diffractive_order)) {
        if (typeof value === "string" && value.startsWith("=")) {
          locationMap[value.substring(1)] = `diffractive_order.${key}`;
        }
      }
    }

    const center_coord = [ffwfDx, ffwfDy, ffwfDz];
    const angular_center_angles = [
      angular_center_angle_x,
      angular_center_angle_y
    ];
    const opening_angles = [opening_angle_x, opening_angle_y];

    const arrayProps = [
      { name: "center_coord", data: center_coord },
      { name: "angular_center_angles", data: angular_center_angles },
      { name: "opening_angles", data: opening_angles },
      { name: "euler_angles", data: euler_angles || [] }
    ];

    arrayProps.forEach(prop => {
      prop.data.forEach((value, index) => {
        if (typeof value === "string" && value.startsWith("=")) {
          locationMap[value.substring(1)] = `${prop.name}.${index}`;
        }
      });
    });

    return sweptVariables.map(sv => ({
      ...sv,
      location: locationMap[sv.name] || null
    }));
  };

  /**
   * Handles the creation of a new MTF plot.
   * @param {object} plotDefinition - The plot parameters from MtfPlotGenerator.
   */
  handleGeneratePlot = async plotDefinition => {
    this.setState({ isCreatingPlot: true });
    try {
      await AnalysisApi.createMtfPlot(this.props.jobId, plotDefinition);
      await this.fetchMtfPlots();
    } catch (error) {
      this.setState({
        errorMessage:
          "Failed to generate MTF plot. Please check your parameters and try again."
      });
    } finally {
      this.setState({ isCreatingPlot: false });
    }
  };

  /**
   * Handles the deletion of an MTF plot.
   * @param {number} plotId - The ID of the plot to delete.
   */
  handleDeletePlot = async plotId => {
    try {
      await AnalysisApi.deleteMtfPlot(this.props.jobId, plotId);
      await this.fetchMtfPlots();
    } catch (error) {
      this.setState({
        errorMessage: "Failed to delete the MTF plot. Please try again."
      });
    }
  };

  /**
   * Toggles the visibility of the diffraction limit line on a specific plot.
   * @param {number} plotId - The ID of the plot to toggle.
   */
  handleToggleDiffractionLimit = plotId => {
    this.setState(prevState => ({
      showDiffractionLimit: {
        ...prevState.showDiffractionLimit,
        [plotId]: !prevState.showDiffractionLimit[plotId]
      }
    }));
  };

  handleCloseErrorSnackbar = () => {
    this.setState({ errorMessage: null });
  };

  /**
   * Sets the initial selected value for each swept variable (defaults to the first value).
   */
  setSelectedValues() {
    let sweptVariableValues = {};
    for (const variable of this.props.sweptVariables) {
      sweptVariableValues[variable.name] = variable.values[0];
    }
    this.setState({ sweptVariableValues });
  }

  /**
   * it updates a variable's selected value in the state
   * @param {*} sweptVariableValues - the updated variable values
   */
  setSelectedValue = sweptVariableValues => {
    this.setState({ sweptVariableValues }, this.getResult);
  };

  /**
   * @param {String} params - the start, stop and step parameters divided by separators
   * @returns {Number[]} - an array containing all parameters
   */
  splitVariableParams = params => {
    const splittedParameters = params.split(/,|;|:/);
    return splittedParameters.map(parameter => parseFloat(parameter));
  };

  /**
   * Handles switching between the "Wavefronts" and "MTF Analysis" views.
   */
  handlePlotViewChange = (event, newView) => {
    if (newView !== null) {
      this.setState({ activePlotView: newView });
    }
  };

  /**
   * Checks if a given swept variable is used in general simulation parameters.
   */
  ifGeneralSweepUsedParameter = sv => {
    const { outgoingConditions, setPoint } = this.props;
    const order_out_x = outgoingConditions.order_out[0],
      order_out_y = outgoingConditions.order_out[1],
      diffractive_order_x = setPoint.diffractive_order["X"],
      diffractive_order_y = setPoint.diffractive_order["Y"],
      {
        amplitude,
        azimut,
        beam_divergence,
        focal_spot_x,
        focal_spot_y,
        focal_spot_z,
        incident_light_type,
        wavelength,
        zenit
      } = setPoint.incident_light,
      general_parameters = [
        zenit,
        azimut,
        wavelength,
        amplitude,
        diffractive_order_x,
        diffractive_order_y,
        order_out_x,
        order_out_y
      ],
      gb_parameters = [
        beam_divergence,
        focal_spot_x,
        focal_spot_y,
        focal_spot_z
      ];
    if (incident_light_type == "PW") {
      return general_parameters.some(
        param =>
          param?.toString().includes("=") &&
          param.toString().split("=")[1] == sv.name
      );
    } else {
      // GB
      return (
        general_parameters.some(
          param =>
            param?.toString().includes("=") &&
            param.toString().split("=")[1] == sv.name
        ) ||
        gb_parameters.some(
          param =>
            param?.toString().includes("=") &&
            param.toString().split("=")[1] == sv.name
        )
      );
    }
  };

  /**
   * Checks if a given swept variable is a far-field wavefront parameter.
   */
  isFFWFParameter = sv => {
    const {
      ffwfDx,
      ffwfDy,
      ffwfDz,
      columns_count,
      rows_count,
      ffwfHeight,
      ffwfWidth
    } = this.props;
    const ffwfParameters = [
      ffwfDx,
      ffwfDy,
      ffwfDz,
      columns_count,
      rows_count,
      ffwfHeight,
      ffwfWidth
    ];
    return ffwfParameters.some(
      param =>
        param?.toString().includes("=") &&
        param.toString().split("=")[1] == sv.name
    );
  };

  /**
   * it shows a spinner, gets the result and hides the spinner again.
   * farfield wavefront has to be wrapped in a matrix because the wavefront component expects
   * 2 matrices.
   */
  getResult = async () => {
    this.setState({ loadingResult: true });
    const result = await AnalysisApi.getAnalysisJobResult(
      this.props.jobId,
      this.state.sweptVariableValues
    );
    const {
      ffwf,
      nfwf,
      fourier,
      width,
      height,
      ff_efficiency,
      nf_efficiency,
      fourier_efficiency,
      rows_count,
      columns_count,
      fourier_theta_angles,
      ffwf_image,
      nfwf_phase_image,
      nfwf_amplitude_image,
      fourier_image,
      nfwf_shape,
      ffwf_shape,
      fourier_shape,
      cropped_nfwf_width,
      cropped_nfwf_height,
      nf_center_x,
      nf_center_y
    } = result.results[0];

    let newState = {
      loadingResult: false,
      nfwf,
      ffwfWidth: width,
      ffwfHeight: height,
      nfEfficiency: nf_efficiency,
      ffEfficiency: ff_efficiency,
      rows_count,
      columns_count,
      fourier_theta_angles,
      ffwf_image,
      nfwf_amplitude_image,
      fourier_image,
      nfwf_shape,
      ffwf_shape,
      fourier_shape,
      cropped_nfwf_width,
      cropped_nfwf_height,
      nf_center_x,
      nf_center_y
    };

    if (nfwf_phase_image)
      newState.nfwf_phase_image = new Blob([nfwf_phase_image], {
        type: "image/png"
      });
    if (nfwf_shape) newState.nfwf_shape = JSON.parse(nfwf_shape).splice(1);
    if (ffwf_shape) newState.ffwf_shape = JSON.parse(ffwf_shape);
    if (fourier_shape) newState.fourier_shape = JSON.parse(fourier_shape);
    if (nfwf_amplitude_image)
      newState.nfwf_amplitude_image = new Blob([nfwf_amplitude_image], {
        type: "image/png"
      });
    if (ffwf_image)
      newState.ffwf_image = new Blob([ffwf_image], { type: "image/png" });
    if (fourier_image)
      newState.fourier_image = new Blob([fourier_image], {
        type: "image/png"
      });

    newState.fourier = fourier
      ? [fourier, ["fake phase because it is not implemented for fourier"]]
      : null;
    newState.ffwf = ffwf
      ? [ffwf, ["fake phase because it is not implemented for ffwf"]]
      : null;
    newState.ffEfficiency = ff_efficiency ?? null;
    newState.fourrierEfficiency = fourier_efficiency ?? null;

    this.setState(newState);
  };

  /**
   * it shows a spinner, gets the cross section result and hides the spinner again.
   */
  getFfwfCrossSectionData = async (
    cross_section_orientation = "xz",
    cross_section_index = 0
  ) => {
    this.setState({
      loadingResult: true,
      ffwf_cross_section_orientation: cross_section_orientation,
      ffwf_cross_section_index: cross_section_index
    });

    let crossSectionSweptVariables = this.state.sweptVariableValues;
    const isDzSwept = this.props.ffwfDz?.toString()[0] === "=";
    if (isDzSwept) {
      crossSectionSweptVariables = Object.fromEntries(
        Object.entries(crossSectionSweptVariables).filter(
          ([key]) => key !== this.props.ffwfDz?.toString().substring(1)
        )
      );
    }
    const result = await AnalysisApi.getAnalysisCrossSectionData(
      this.props.jobId,
      crossSectionSweptVariables,
      "ffwf",
      cross_section_orientation,
      cross_section_index
    );
    let newState = {
      loadingResult: false
    };
    this.setState(newState);
    return result?.data;
  };

  /**
   * Fetches cross-section data for the Fourier wavefront.
   */
  getFourierWFCrossSectionData = async (
    cross_section_orientation = "xz",
    cross_section_index = 0
  ) => {
    this.setState({
      loadingResult: true,
      fourier_cross_section_orientation: cross_section_orientation,
      fourier_cross_section_index: cross_section_index
    });

    let crossSectionSweptVariables = this.state.sweptVariableValues;
    const result = await AnalysisApi.getAnalysisCrossSectionData(
      this.props.jobId,
      crossSectionSweptVariables,
      "fourier",
      cross_section_orientation,
      cross_section_index
    );
    let newState = {
      loadingResult: false
    };
    this.setState(newState);
    return result?.data;
  };

  setNfwfCrossSectionType = nfwf_cross_section_type => {
    this.setState({
      nfwf_cross_section_type
    });
  };

  /**
   * it shows a spinner, gets the cross section result and hides the spinner again.
   */
  getNfwfCrossSectionData = async (
    cross_section_orientation = "xz",
    cross_section_index = 0,
    nfwf_cross_section_type
  ) => {
    this.setState({
      loadingResult: true,
      nfwf_cross_section_orientation: cross_section_orientation,
      nfwf_cross_section_index: cross_section_index
    });
    let crossSectionSweptVariables = this.state.sweptVariableValues;
    crossSectionSweptVariables = Object.fromEntries(
      Object.entries(crossSectionSweptVariables).filter(
        ([key]) =>
          !this.isFFWFParameter({ name: key }) ||
          this.ifGeneralSweepUsedParameter({ name: key })
      )
    );
    const result = await AnalysisApi.getAnalysisCrossSectionData(
      this.props.jobId,
      crossSectionSweptVariables,
      "nfwf",
      cross_section_orientation,
      cross_section_index,
      (nfwf_cross_section_type =
        nfwf_cross_section_type ?? this.state.nfwf_cross_section_type)
    );
    let newState = {
      loadingResult: false
    };
    this.setState(newState);
    return result?.data;
  };

  /**
   * @returns {Boolean} whether variables were used in the analysis
   */
  isMultipleResult() {
    return this.props.sweptVariables.length > 0;
  }

  getFFWFCoordinate(coordinate) {
    if (isNaN(coordinate)) {
      const variableName = coordinate.substring(1);
      return this.state.sweptVariableValues[variableName];
    }
    return parseFloat(coordinate);
  }

  getXCenter() {
    const { ffwfDx } = this.props;
    return this.getFFWFCoordinate(ffwfDx);
  }

  getFourierXCenter() {
    return this.getFFWFCoordinate(this.props.angular_center_angle_x);
  }

  getYCenter() {
    const { ffwfDy } = this.props;
    return this.getFFWFCoordinate(ffwfDy);
  }

  getFourierYCenter() {
    const { angular_center_angle_y } = this.props;
    return this.getFFWFCoordinate(angular_center_angle_y);
  }

  getFourierOpeningAngleX() {
    const { opening_angle_x } = this.props;
    return this.getFFWFCoordinate(opening_angle_x);
  }

  getFourierOpeningAngleY() {
    const { opening_angle_y } = this.props;
    return this.getFFWFCoordinate(opening_angle_y);
  }

  render() {
    const {
      classes,
      sweptVariables,
      nfwfWidth,
      nfwfHeight,
      nfwfUnit,
      angularUnit,
      has_mtf_analysis,
      nf_width,
      nf_height,
      nf_unit
    } = this.props;

    const {
      ffwf,
      nfwf,
      fourier,
      loadingResult,
      ffwfHeight,
      ffwfWidth,
      ffEfficiency,
      nfEfficiency,
      fourrierEfficiency,
      fourier_theta_angles,
      ffwf_cross_section_orientation,
      ffwf_cross_section_index,
      nfwf_cross_section_orientation,
      nfwf_cross_section_index,
      fourier_cross_section_orientation,
      fourier_cross_section_index,
      nfwf_phase_image,
      nfwf_amplitude_image,
      fourier_image,
      ffwf_image,
      ffwf_shape,
      nfwf_shape,
      fourier_shape,
      activePlotView,
      mtfPlots,
      loadingMtfPlots,
      isCreatingPlot,
      showDiffractionLimit,
      errorMessage,
      cropped_nfwf_width,
      cropped_nfwf_height,
      nf_center_x,
      nf_center_y
    } = this.state;

    const singleWavefront =
      ((ffwf || ffwf_image) &&
        !(nfwf || nfwf_phase_image || nfwf_amplitude_image) &&
        !(fourier || fourier_image)) ||
      (!(ffwf || ffwf_image) &&
        (nfwf || nfwf_phase_image || nfwf_amplitude_image) &&
        !(fourier || fourier_image)) ||
      (!(ffwf || ffwf_image) &&
        !(nfwf || nfwf_phase_image || nfwf_amplitude_image) &&
        (fourier || fourier_image));

    const centerGridStyle = { margin: "auto" };

    const sweptVariablesWithLocations = this.getSweptVariablesWithLocations();

    return (
      <div className={classes.root}>
        <Typography className={classes.title} variant="h5" component="h3">
          {"Result"}
        </Typography>

        <Grid container spacing={2} style={{ marginTop: 20 }}>
          <Grid item xs={12}>
            <Grid container style={{ width: "60%", margin: "auto" }}>
              {this.isMultipleResult() && (
                <Grid item xs={12}>
                  <SweepPoint
                    sweptVariables={sweptVariables.filter(sv => {
                      return !ffwf
                        ? this.isFFWFParameter(sv) &&
                          !this.ifGeneralSweepUsedParameter(sv)
                          ? false
                          : true
                        : true;
                    })}
                    sweptVariableValues={this.state.sweptVariableValues}
                    setSelectedValue={this.setSelectedValue}
                    onChangeCommitted={() => {
                      this.getResult();
                    }}
                  />
                </Grid>
              )}
              <Grid item xs={12} style={{ marginBottom: 20 }}>
                {loadingResult && (
                  <Spinner
                    size={25}
                    className={classes.centerElement}
                    timeout={30000}
                  />
                )}
              </Grid>
              <Grid container direction="row" style={{ marginBottom: 20 }}>
                <Grid item xs={6}>
                  <Grid item xs={6}>
                    <Efficiency
                      efficiency={nfEfficiency}
                      type={"NFWF"}
                      test-data={"nfEfficiency"}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Efficiency
                      efficiency={ffEfficiency}
                      type={"FFWF"}
                      test-data={"ffEfficiency"}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Efficiency
                      efficiency={fourrierEfficiency}
                      type={"Fourier"}
                      test-data={"fourrierEfficiency"}
                    />
                  </Grid>
                </Grid>
                <Grid item xs={6}>
                  {has_mtf_analysis && (
                    <ToggleButtonGroup
                      value={activePlotView}
                      exclusive
                      onChange={this.handlePlotViewChange}
                    >
                      <CustomToggleButton value="results">
                        Wavefronts
                      </CustomToggleButton>
                      <CustomToggleButton value="mtf">
                        MTF Analysis
                      </CustomToggleButton>
                    </ToggleButtonGroup>
                  )}
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12}>
          {/* Wrapper for Wavefronts View */}
          <div
            className={classes.viewContainer}
            style={{
              display: activePlotView === "results" ? "block" : "none"
            }}
          >
            <Grid container>
              {fourier && (
                <Grid
                  item
                  xs={singleWavefront ? 8 : 4}
                  className={singleWavefront ? classes.centeredWavefront : ""}
                >
                  <GenericWavefront
                    title={"Fourier Wavefront"}
                    hidePhase
                    wavefront={fourier}
                    wfWidth={this.getFourierOpeningAngleX()}
                    wfHeight={this.getFourierOpeningAngleY()}
                    showLegend
                    enableCrossSection
                    rowsCount={fourier_theta_angles["X"].length}
                    columnsCount={fourier_theta_angles["Y"].length}
                    crossSectionOrientation={fourier_cross_section_orientation}
                    crossSectionIndex={fourier_cross_section_index}
                    handleCrossSection={this.getFourierWFCrossSectionData}
                    hideHeatMapCrossSectionOrientationLabel={true}
                    xLabel={`x-angle (${angularUnit})`}
                    wavefrontXData={fourier_theta_angles["X"]}
                    wavefrontYData={fourier_theta_angles["Y"]}
                    yLabel={`y-angle (${angularUnit})`}
                    xCenter={this.getFourierXCenter()}
                    yCenter={this.getFourierYCenter()}
                    sweptVariableValues={this.state.sweptVariableValues}
                  />
                </Grid>
              )}
              {nfwf_phase_image &&
                nfwf_amplitude_image &&
                ffwf_image &&
                !fourier &&
                (!fourier_image || !fourier_shape) && (
                  <DirectionSnackbar
                    message={
                      "Fourier analysis image unavailable, redo the analysis to generate the image"
                    }
                  />
                )}
              {!fourier && fourier_image && fourier_shape && (
                <Grid
                  item
                  xs={singleWavefront ? 8 : 4}
                  style={singleWavefront ? centerGridStyle : null}
                  test-data={"fourierImage"}
                >
                  <GenericImageWavefront
                    title={"Fourier Wavefront"}
                    hidePhase
                    wavefrontImages={[
                      fourier_image,
                      "fake phase because it is not implemented for fourier"
                    ]}
                    wfWidth={this.getFourierOpeningAngleX()}
                    wfHeight={this.getFourierOpeningAngleY()}
                    showLegend
                    enableCrossSection
                    rowsCount={fourier_theta_angles["X"].length}
                    columnsCount={fourier_theta_angles["Y"].length}
                    crossSectionOrientation={fourier_cross_section_orientation}
                    crossSectionIndex={fourier_cross_section_index}
                    handleCrossSection={this.getFourierWFCrossSectionData}
                    hideHeatMapCrossSectionOrientationLabel={true}
                    xCenter={this.getFourierXCenter()}
                    yCenter={this.getFourierYCenter()}
                    wavefront_shape={fourier_shape}
                    sweptVariableValues={this.state.sweptVariableValues}
                  />
                </Grid>
              )}
              {ffwf && (
                <Grid
                  item
                  xs={singleWavefront ? 8 : 4}
                  style={singleWavefront ? centerGridStyle : null}
                  test-data={"ffwf"}
                >
                  <GenericWavefront
                    title={"Far-field wavefront"}
                    hidePhase
                    wavefront={ffwf}
                    wfWidth={
                      (ffwfWidth * (this.props.columns_count - 1)) /
                      this.props.columns_count
                    }
                    wfHeight={
                      (ffwfHeight * (this.props.rows_count - 1)) /
                      this.props.rows_count
                    }
                    xCenter={this.getXCenter()}
                    yCenter={this.getYCenter()}
                    unit={this.props.ffwfUnit}
                    showLegend
                    enableCrossSection
                    crossSectionOrientation={ffwf_cross_section_orientation}
                    crossSectionIndex={ffwf_cross_section_index}
                    handleCrossSection={this.getFfwfCrossSectionData}
                    sweptVariableValues={this.state.sweptVariableValues}
                  />
                </Grid>
              )}
              {!ffwf && ffwf_image && ffwf_shape && (
                <Grid
                  item
                  xs={singleWavefront ? 8 : 4}
                  style={singleWavefront ? centerGridStyle : null}
                  test-data={"ffwfImage"}
                >
                  <GenericImageWavefront
                    title={"Far-Field Wavefront"}
                    hidePhase
                    wavefrontImages={[
                      ffwf_image,
                      "fake phase because it is not implemented for ffwf"
                    ]}
                    wfWidth={ffwfWidth}
                    wfHeight={ffwfHeight}
                    unit={this.props.ffwfUnit}
                    showLegend
                    enableCrossSection
                    crossSectionOrientation={ffwf_cross_section_orientation}
                    crossSectionIndex={ffwf_cross_section_index}
                    handleCrossSection={this.getFfwfCrossSectionData}
                    hideHeatMapCrossSectionOrientationLabel={true}
                    xCenter={this.getXCenter()}
                    yCenter={this.getYCenter()}
                    wavefront_shape={ffwf_shape}
                    sweptVariableValues={this.state.sweptVariableValues}
                  />
                </Grid>
              )}
              {ffwf && nfwf && (
                <Grid
                  item
                  xs={singleWavefront ? 8 : 4}
                  style={singleWavefront ? centerGridStyle : null}
                  test-data={"nfwf"}
                >
                  <GenericWavefront
                    title={"Near-field wavefront"}
                    wavefront={nfwf}
                    wfWidth={
                      cropped_nfwf_width
                        ? cropped_nfwf_width * (1 - 1 / nfwf[1][1].length)
                        : nfwfWidth * (1 - 1 / nfwf[1][1].length)
                    }
                    wfHeight={
                      cropped_nfwf_height
                        ? cropped_nfwf_height * (1 - 1 / nfwf[1].length)
                        : nfwfHeight * (1 - 1 / nfwf[1].length)
                    }
                    enableCrossSection
                    crossSectionOrientation={nfwf_cross_section_orientation}
                    crossSectionIndex={nfwf_cross_section_index}
                    handleCrossSection={this.getNfwfCrossSectionData}
                    onWaveFrontSelect={this.setNfwfCrossSectionType}
                    unit={nfwfUnit}
                    showLegend
                    sweptVariableValues={this.state.sweptVariableValues}
                    xCenter={nf_center_x}
                    yCenter={nf_center_y}
                  />
                </Grid>
              )}
              {!nfwf && nfwf_phase_image && nfwf_amplitude_image && nfwf_shape && (
                <Grid
                  item
                  xs={singleWavefront ? 8 : 4}
                  style={singleWavefront ? centerGridStyle : null}
                  test-data={"nfwfImage"}
                >
                  <GenericImageWavefront
                    title={"Near-field wavefront"}
                    wavefrontImages={[nfwf_amplitude_image, nfwf_phase_image]}
                    wfWidth={
                      cropped_nfwf_width ? cropped_nfwf_width : nfwfWidth
                    }
                    wfHeight={
                      cropped_nfwf_height ? cropped_nfwf_height : nfwfHeight
                    }
                    enableCrossSection
                    crossSectionOrientation={nfwf_cross_section_orientation}
                    crossSectionIndex={nfwf_cross_section_index}
                    handleCrossSection={this.getNfwfCrossSectionData}
                    onWaveFrontSelect={this.setNfwfCrossSectionType}
                    unit={nfwfUnit}
                    showLegend
                    wavefront_shape={nfwf_shape}
                    sweptVariableValues={this.state.sweptVariableValues}
                    xCenter={nf_center_x}
                    yCenter={nf_center_y}
                  />
                </Grid>
              )}
              {!ffwf && nfwf && (
                <Wavefront
                  wavefront={nfwf}
                  wfWidth={
                    cropped_nfwf_width
                      ? cropped_nfwf_width * (1 - 1 / nfwf[1][1].length)
                      : nfwfWidth * (1 - 1 / nfwf[1][1].length)
                  }
                  wfHeight={
                    cropped_nfwf_height
                      ? cropped_nfwf_height * (1 - 1 / nfwf[1].length)
                      : nfwfHeight * (1 - 1 / nfwf[1].length)
                  }
                  unit={nfwfUnit}
                  enableCrossSection
                  crossSectionOrientation={nfwf_cross_section_orientation}
                  crossSectionIndex={nfwf_cross_section_index}
                  handleCrossSection={this.getNfwfCrossSectionData}
                  showLegend
                  sweptVariableValues={this.state.sweptVariableValues}
                  xCenter={nf_center_x}
                  yCenter={nf_center_y}
                />
              )}
            </Grid>
          </div>

          {/* Wrapper for MTF Analysis View */}
          {has_mtf_analysis && (
            <div
              className={classes.viewContainer}
              style={{ display: activePlotView === "mtf" ? "block" : "none" }}
            >
              <Grid container direction="column" spacing={3}>
                <Grid
                  item
                  xs={12}
                  style={{ display: "flex", justifyContent: "center" }}
                >
                  <MtfPlotGenerator
                    sweptVariables={sweptVariablesWithLocations}
                    mtfPlots={mtfPlots}
                    onGenerate={this.handleGeneratePlot}
                    isCreating={isCreatingPlot}
                    autoGenerate={
                      sweptVariablesWithLocations.length === 0 &&
                      !isCreatingPlot &&
                      !loadingMtfPlots &&
                      mtfPlots.length === 0
                    }
                    onError={error => {
                      this.setState({ errorMessage: null }, () => {
                        this.setState({ errorMessage: error });
                      });
                    }}
                  />
                </Grid>

                {loadingMtfPlots && (
                  <Grid item xs={12}>
                    <Spinner className={classes.centerElement} />
                  </Grid>
                )}

                {!loadingMtfPlots && mtfPlots.length === 0 && (
                  <Grid
                    item
                    xs={12}
                    style={{ textAlign: "center", marginTop: 32 }}
                  >
                    <Typography color="textSecondary">
                      No MTF plots generated yet. Use the controls above to
                      create one.
                    </Typography>
                  </Grid>
                )}

                {mtfPlots.map(plot => {
                  // Add defensive checks for plot data
                  if (
                    !plot ||
                    !plot.xData ||
                    !plot.yData ||
                    plot.xData.length === 0 ||
                    plot.yData.length === 0
                  ) {
                    return (
                      <Grid item xs={12} key={plot.id || Math.random()}>
                        <Paper className={classes.plotContainer}>
                          <Typography color="error">
                            Invalid plot data. Please regenerate this plot.
                          </Typography>
                        </Paper>
                      </Grid>
                    );
                  }

                  return (
                    <Grid item xs={12} key={plot.id}>
                      <Paper className={classes.plotContainer}>
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                        >
                          <Typography variant="h6" gutterBottom>
                            {plot.yName || "MTF Plot"}
                          </Typography>
                          <Box>
                            <Box display="flex" alignItems="center">
                              <Tooltip
                                title={
                                  <div style={{ whiteSpace: "pre-line" }}>
                                    {
                                      "The diffraction-limited MTF is calculated based on the component's parameters. It represents the theoretical maximum MTF for the given aperture.\n\nThe calculation uses the following formula:\n`cutoff_freq = (refractive_index * D) / (wavelength * propagation_distance)`\n`MTF = 1 - (spatial_freq / cutoff_freq)`\n\nwhere 'D' is the characteristic dimension of the aperture."
                                    }
                                  </div>
                                }
                              >
                                <IconButton
                                  size="small"
                                  style={{
                                    padding: "2px",
                                    marginRight: "4px"
                                  }}
                                >
                                  <HelpOutline fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Button
                                size="small"
                                onClick={() =>
                                  this.handleToggleDiffractionLimit(plot.id)
                                }
                                variant={
                                  showDiffractionLimit[plot.id]
                                    ? "contained"
                                    : "outlined"
                                }
                              >
                                Diffraction Limit
                              </Button>
                              <IconButton
                                aria-label="delete plot"
                                onClick={() => this.handleDeletePlot(plot.id)}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Box>
                          </Box>
                        </Box>
                        <div className={classes.plotWrapper}>
                          {(() => {
                            try {
                              const formattedData = PlotUtils.formatDataForPlotbox(
                                plot,
                                showDiffractionLimit[plot.id],
                                sweptVariables
                              );
                              const plotboxes = PlotUtils.getPlotboxFromFormattedData(
                                plot,
                                formattedData
                              );

                              return (
                                <>
                                  <PlotActions
                                    plotboxes={plotboxes}
                                    id={plot.id}
                                    simulationName={plot.yName}
                                    projectName={plot.yName}
                                    username={this.props.username}
                                    getCSV={Helper.getMultiplePlotsCSVData}
                                    multiple={true}
                                  />
                                  <Plotbox
                                    data={formattedData}
                                    params={[["All"], {}]}
                                    configuration={{ incident_light: {} }}
                                    sweptVarNames={
                                      plot.sliderName && plot.sliderName !== ""
                                        ? [plot.sliderName]
                                        : []
                                    }
                                    onPlotboxChange={() => {}}
                                    onRangeValueChange={() => {}}
                                  />
                                </>
                              );
                            } catch (error) {
                              console.error(
                                "Error formatting plot data:",
                                error,
                                plot
                              );
                              return (
                                <Typography color="error">
                                  Error displaying plot. Please regenerate this
                                  plot.
                                </Typography>
                              );
                            }
                          })()}
                        </div>
                      </Paper>
                    </Grid>
                  );
                })}
              </Grid>
            </div>
          )}
        </Grid>

        {errorMessage && (
          <DirectionSnackbar
            message={errorMessage}
            onClose={this.handleCloseErrorSnackbar}
          />
        )}

        {nfEfficiency !== null &&
          nfEfficiency !== undefined &&
          nfEfficiency > 1 && (
            <DirectionSnackbar
              message={
                "The near-field efficiency calculation shows optical gain.\nCheck the meta-cell definition to make sure this is intended."
              }
            />
          )}
        {ffEfficiency !== null &&
          ffEfficiency !== undefined &&
          nfEfficiency !== null &&
          nfEfficiency !== undefined &&
          ffEfficiency > nfEfficiency && (
            <DirectionSnackbar
              message={
                "The far field calculation is not well resolved and the calculated\nFFWF efficiency and Intensity may be inaccurate.\nConsider the following to improve accuracy:\n\t1) Increase the number of pixels\n\t2) Set an odd number of pixels\n\t3) Decrease the size of the far-field"
              }
            />
          )}
      </div>
    );
  }
}
export default withErrorBoundary(withStyles(styles)(AnalysisJobResult));
